import { Component, Inject, OnInit, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { ToolbarService } from '../../../services/toolbar.service';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { QuotingSystemCreate, QuotingSystemUpdate } from '../../../model/quoting-system/quoting-system-create.model';
import { EscapeDialogHelper } from '../../../helpers/escape-dialog.helper';
import { SysAdminService } from '../../../services/sys-admin.service';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';
import { PhoneValidator } from '../../../validators/phone.validator';
import { getErrorMessage } from '../../../consts/form-field-errors';

@Component({
  selector: 'app-add-quoting-system',
  templateUrl: './add-quoting-system.component.html',
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatDivider,
    CdkScrollable,
    MatDialogContent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatError,
    MatDialogActions,
    MatButton
  ]
})
export class AddQuotingSystemComponent implements OnInit {
  quotingSystemForm: UntypedFormGroup;
  pending = signal<boolean>(false);
  protected readonly getErrorMessage = getErrorMessage;

  constructor(
    public dialogRef: MatDialogRef<AddQuotingSystemComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: UntypedFormBuilder,
    public toolbarService: ToolbarService,
    private message: MessagingService,
    private escapeDialogRef: EscapeDialogHelper,
    private sysAdminService: SysAdminService
  ) {}

  get contactPhoneNumber() {
    return this.quotingSystemForm.get('contactPhoneNumber');
  }
  get companyName() {
    return this.quotingSystemForm.get('companyName');
  }

  ngOnInit(): void {
    this.quotingSystemForm = this.fb.group({
      companyName: [
        this.data && this.data?.quotingSystem?.clientId ? this.data?.quotingSystem?.companyName : '',
        [Validators.required]
      ],
      contactPhoneNumber: [
        this.data && this.data?.quotingSystem?.clientId ? this.data?.quotingSystem?.contactPhoneNumber : '',
        PhoneValidator
      ]
    });
    this.escapeDialogRef.escapeDialog(this.dialogRef);
    this.contactPhoneNumber.markAsTouched();
    this.companyName.valueChanges.subscribe(() => {
      if (this.companyName.invalid) {
        this.companyName.markAsTouched();
      }
    });
  }

  addQuotingSystem() {
    this.pending.set(true);
    this.toolbarService.setCloudState('SAVING');
    const newQSystem: QuotingSystemCreate = {
      companyName: this.companyName.value.trim(),
      contactPhoneNumber: this.contactPhoneNumber.value.trim()
    };
    this.sysAdminService.createQuotingSystem(newQSystem).subscribe(
      () => this.dialogRef.close('Success'),
      (error) => {
        this.message.error('Could not add quoting system.');
        this.toolbarService.setCloudState('RESTING');
        this.pending.set(false);
      },
      () => {
        this.toolbarService.setCloudState('RESTING');
        this.pending.set(false);
      }
    );
  }

  editQuotingSystem() {
    this.pending.set(true);
    const quotingSystemData: QuotingSystemUpdate = {};
    quotingSystemData.companyName = this.companyName.value.trim();
    quotingSystemData.contactPhoneNumber = this.contactPhoneNumber.value.trim();

    this.toolbarService.setCloudState('SAVING');
    this.sysAdminService.updateQuotingSystem(this.data.quotingSystem.clientId, quotingSystemData).subscribe(
      () => this.dialogRef.close('Success'),
      (error) => {
        this.message.error('Could not update quoting system.');
        this.toolbarService.setCloudState('RESTING');
        this.pending.set(false);
      },
      () => {
        this.toolbarService.setCloudState('RESTING');
        this.pending.set(false);
      }
    );
  }
}
